import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { EventSeverity } from "@safers/api";
import { createPortal } from "react-dom";
import { useInfiniteList } from "@/hooks/useInfiniteList";
import { useAlarmInbox, type AlarmItem } from "@/hooks/useAlarmInbox";
import { useEventModal } from "@/hooks/useEventModal";
import { SEVERITY_COLORS, SEVERITY_LABELS } from "@/lib/map";
import { SEVERITY_PALETTE } from "@safers/ui";
import { severityChip } from "@/services";

type Filter = "전체" | EventSeverity;

// 조치 대상이 아닌 등급은 뺀다. 손으로 나열하면 등급이 늘어날 때 조용히 빠지므로
// (실제로 NORMAL 이 그랬다) 팔레트에서 파생시키고 제외 목록만 둔다.
const FILTER_EXCLUDED: EventSeverity[] = ["NORMAL", "UNCERTAIN"];
const FILTERS: Filter[] = [
  "전체",
  ...(Object.keys(SEVERITY_PALETTE) as EventSeverity[]).filter((s) => !FILTER_EXCLUDED.includes(s)),
];

function label(f: Filter): string {
  return f === "전체" ? "전체" : SEVERITY_LABELS[f];
}

function timeAgo(ts: string): string {
  const t = new Date(ts).getTime();
  if (!Number.isFinite(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "방금";
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  return `${Math.floor(hr / 24)}일 전`;
}

type Variant = "header" | "fab";

export function AlarmInboxBell({
  tone = "light",
  variant = "header",
}: {
  tone?: "light" | "dark";
  variant?: Variant;
}) {
  const { items, unreadCount, markSeen } = useAlarmInbox();
  const [newIds, setNewIds] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<Filter>("전체");
  /*
    상세는 **여기서 그리지 않는다.** 한때 이 팝오버 안에 마스터-디테일로 넣어 봤는데,
    같은 이벤트를 관제 이벤트 표에서 누르면 모달, 여기서 누르면 팝오버가 떠서 **진입점에
    따라 다른 UI** 가 나왔다. 상세는 `EventDetailSheet` 하나로 모으고 인박스는 목록만
    맡는다. 팝오버가 2단이 되면서 커지던 포커스 관리 부담도 같이 사라진다.
  */
  const { openEvent } = useEventModal();
  const rootRef = useRef<HTMLDivElement>(null);
  // fab 변형은 목록을 body 로 내보낸다(아래 참고) — 그때는 트리거 밖이라 따로 봐야 한다.
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!rootRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = useMemo(
    () => (filter === "전체" ? items : items.filter((i) => i.severity === filter)),
    [items, filter]
  );
  const { count, sentinelRef, hasMore } = useInfiniteList(filtered.length, filter, 25);

  function toggle() {
    setOpen((v) => {
      if (v) return false;
      setNewIds(new Set(items.filter((i) => !i.read).map((i) => i.eventId)));
      markSeen();
      return true;
    });
  }

  // 상세를 열면 팝오버는 닫는다 — 드로어가 뜬 뒤에도 알림함이 남아 있으면 겹쳐 거슬린다.
  function handleClick(item: AlarmItem) {
    setOpen(false);
    openEvent(item.id);
  }

  const dark = tone === "dark";
  const fab = variant === "fab";
  /*
    fab 은 하단 탭 바 안에 있는데, 그 바는 본문의 스크롤 그림자를 넘으려고 `z-40` 을 갖는다.
    `z-index` 는 쌓임 맥락을 만들므로 목록의 `z-[1000]` 이 그 안에 갇혀, 페이지 최상위의
    이벤트 알람 배너(`z-[100]`)가 목록의 등급 필터 위를 덮었다. body 로 내보내 풀어 준다.
  */
  const renderPanel = (node: ReactNode) => (fab ? createPortal(node, document.body) : node);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-label={unreadCount > 0 ? `알림 ${unreadCount}건` : "알림"}
        aria-expanded={open}
        className={
          fab
            ? "relative flex size-[3.25rem] items-center justify-center rounded-full bg-brand text-white shadow-[0_0.3125rem_0.75rem_-0.125rem_rgba(64,28,0,0.55)] transition-colors hover:bg-brand-hover"
            : `relative flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
                dark
                  ? "text-white/70 hover:bg-white/10"
                  : "text-neutral-600 hover:bg-neutral-200/60"
              }`
        }
      >
        <svg
          width={fab ? 24 : 18}
          height={fab ? 24 : 18}
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" />
        </svg>
        {unreadCount > 0 && (
          <span
            className={`absolute flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[0.5625rem] font-bold text-white ${
              fab ? "-right-0.5 top-0 ring-2 ring-[var(--surface-base)]" : "-right-0.5 -top-0.5"
            }`}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open &&
        renderPanel(
          <div
            ref={panelRef}
            className={
              /*
                모바일 목록 높이는 **화면의 70%** 로 끊는다. 전에는 `100dvh - 9rem`(844px
                화면에서 83%)이라 위쪽 끝까지 차올라 뒤 화면이 안 보였다 — 그러면 목록이
                아니라 지면처럼 읽히고, 어디서 열었는지 감각을 잃는다. 뒤가 보여야
                "잠깐 열어 본 것"이 된다.
              */
              fab
                ? "fixed inset-x-2 bottom-[calc(5rem_+_env(safe-area-inset-bottom))] z-[1000] flex max-h-[70dvh] flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-xl"
                : "fixed inset-x-2 top-[3.5rem] z-[1000] overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-10 sm:w-[24rem]"
            }
          >
            <div className="flex items-center justify-between px-4 pb-2 pt-3">
              <span className="text-[0.9375rem] font-bold text-neutral-900">알림</span>
              <span className="text-xs text-neutral-400">
                {newIds.size > 0 ? `새 알림 ${newIds.size}건` : "새 알림 없음"}
              </span>
            </div>

            <div className="flex flex-wrap gap-1.5 border-b border-neutral-100 px-4 pb-3">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`shrink-0 rounded-full px-3 py-1 text-[0.6875rem] transition-colors ${
                    filter === f
                      ? "bg-neutral-900 text-white"
                      : "border border-neutral-200 text-neutral-500 hover:bg-neutral-50"
                  }`}
                >
                  {label(f)}
                </button>
              ))}
            </div>

            <ul
              className={`min-h-0 flex-1 overflow-y-auto ${fab ? "" : "max-h-[min(22rem,60vh)]"}`}
            >
              {filtered.length === 0 ? (
                <li className="flex flex-col items-center gap-1 px-4 py-10">
                  <span className="text-[0.8125rem] font-bold text-neutral-900">
                    알림이 없습니다
                  </span>
                  <span className="text-xs text-neutral-400">
                    이벤트가 감지되면 여기에 표시됩니다.
                  </span>
                </li>
              ) : (
                filtered.slice(0, count).map((item) => (
                  <li key={item.eventId}>
                    <button
                      type="button"
                      onClick={() => handleClick(item)}
                      className={`flex w-full items-center gap-2.5 border-b border-neutral-100 px-4 py-3 text-left last:border-b-0 hover:bg-neutral-50 ${
                        newIds.has(item.eventId) ? "bg-orange-50/40" : ""
                      }`}
                    >
                      <span
                        className="h-1.5 w-1.5 flex-none rounded-full"
                        style={{
                          background: newIds.has(item.eventId)
                            ? (item.severity && SEVERITY_COLORS[item.severity]) ||
                              SEVERITY_COLORS.UNCERTAIN
                            : "transparent",
                        }}
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          {item.severity && (
                            <span
                              className={`flex-none rounded px-1.5 py-0.5 text-[0.625rem] font-bold ${severityChip(item.severity).bg} ${severityChip(item.severity).text}`}
                            >
                              {SEVERITY_LABELS[item.severity]}
                            </span>
                          )}
                          <span className="truncate text-[0.8125rem] font-semibold text-neutral-900">
                            {item.name}
                          </span>
                        </span>
                        <span className="mt-0.5 block truncate text-[0.6875rem] text-neutral-400">
                          {item.siteName ?? "현장 미상"} · {timeAgo(item.timestamp)}
                        </span>
                      </span>
                      <span className="flex-none text-neutral-300">›</span>
                    </button>
                  </li>
                ))
              )}
              {hasMore && (
                <li
                  ref={sentinelRef as React.RefObject<HTMLLIElement>}
                  className="h-8"
                  aria-hidden="true"
                />
              )}
            </ul>

            <div className="flex-none bg-neutral-50 px-4 py-2 text-[0.6875rem] text-neutral-400">
              {fab ? "누르면" : "클릭하면"} 이벤트 상세가 열립니다
            </div>
          </div>
        )}
    </div>
  );
}
